import {
  Box,
  Divider,
  Group,
  Paper,
  SegmentedControl,
  Slider,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import React from 'react';

import { useVectorCadStore } from '../stores/vector-cad-store';
import type { BackerType } from '../types';

export function BackerPanel() {
  const { backerConfig, updateBackerConfig } = useVectorCadStore();

  return (
    <Stack gap='md'>
      <Title order={5}>Base, Backer & Mounts</Title>

      {/* Backer Type Segmented Control */}
      <SegmentedControl
        fullWidth
        size='xs'
        value={backerConfig.type}
        onChange={(val) => updateBackerConfig({ type: val as BackerType })}
        data={[
          { label: 'None', value: 'none' },
          { label: 'Outset Backer', value: 'outset' },
          { label: 'Base Bar', value: 'base-bar' },
          { label: 'Cake Topper', value: 'cake-topper' },
          { label: 'Frame', value: 'frame-oval' },
        ]}
      />

      {/* Outset Backer Settings */}
      {backerConfig.type === 'outset' && (
        <Paper withBorder p='sm' radius='md' style={{ background: 'var(--mantine-color-dark-7)' }}>
          <Stack gap='sm'>
            <Text size='sm' fw={600}>
              Outset Backing Plate
            </Text>

            <Box>
              <Group justify='space-between' mb={2}>
                <Text size='xs' c='dimmed'>
                  Offset Margin
                </Text>
                <Text size='xs' fw={600}>
                  +{backerConfig.offsetMm} mm
                </Text>
              </Group>
              <Slider
                size='xs'
                min={1.0}
                max={15.0}
                step={0.5}
                value={backerConfig.offsetMm}
                onChange={(val) => updateBackerConfig({ offsetMm: val })}
              />
            </Box>

            <Group justify='space-between'>
              <div>
                <Text size='xs' fw={500}>
                  Score Alignment Guides on Backer
                </Text>
                <Text size='10px' c='dimmed'>
                  Lightly scores letter positions for easy gluing
                </Text>
              </div>
              <Switch
                size='sm'
                checked={backerConfig.scoreGuidesOnBacker}
                onChange={(e) =>
                  updateBackerConfig({ scoreGuidesOnBacker: e.currentTarget.checked })
                }
              />
            </Group>

            <Divider my={2} />

            <Group justify='space-between'>
              <div>
                <Text size='xs' fw={500}>
                  Hanger Mounting Holes
                </Text>
                <Text size='10px' c='dimmed'>
                  Adds top holes for string / wall hanging
                </Text>
              </div>
              <Switch
                size='sm'
                checked={backerConfig.hangerHoles}
                onChange={(e) => updateBackerConfig({ hangerHoles: e.currentTarget.checked })}
              />
            </Group>

            {backerConfig.hangerHoles && (
              <Box>
                <Group justify='space-between' mb={2}>
                  <Text size='xs' c='dimmed'>
                    Hole Diameter
                  </Text>
                  <Text size='xs' fw={600}>
                    {backerConfig.hangerHoleDiameterMm} mm
                  </Text>
                </Group>
                <Slider
                  size='xs'
                  min={2.0}
                  max={8.0}
                  step={0.5}
                  value={backerConfig.hangerHoleDiameterMm}
                  onChange={(val) => updateBackerConfig({ hangerHoleDiameterMm: val })}
                />
              </Box>
            )}
          </Stack>
        </Paper>
      )}

      {/* Base Bar Settings */}
      {backerConfig.type === 'base-bar' && (
        <Paper withBorder p='sm' radius='md' style={{ background: 'var(--mantine-color-dark-7)' }}>
          <Stack gap='sm'>
            <Text size='sm' fw={600}>
              Freestanding Base Bar
            </Text>

            <Box>
              <Group justify='space-between' mb={2}>
                <Text size='xs' c='dimmed'>
                  Bar Height
                </Text>
                <Text size='xs' fw={600}>
                  {backerConfig.baseBarHeightMm} mm
                </Text>
              </Group>
              <Slider
                size='xs'
                min={6}
                max={30}
                step={1}
                value={backerConfig.baseBarHeightMm}
                onChange={(val) => updateBackerConfig({ baseBarHeightMm: val })}
              />
            </Box>

            <Box>
              <Group justify='space-between' mb={2}>
                <Text size='xs' c='dimmed'>
                  Horizontal Extension
                </Text>
                <Text size='xs' fw={600}>
                  +{backerConfig.baseBarExtendMm} mm
                </Text>
              </Group>
              <Slider
                size='xs'
                min={0}
                max={25}
                step={1}
                value={backerConfig.baseBarExtendMm}
                onChange={(val) => updateBackerConfig({ baseBarExtendMm: val })}
              />
            </Box>
          </Stack>
        </Paper>
      )}

      {/* Cake Topper Stake Settings */}
      {backerConfig.type === 'cake-topper' && (
        <Paper withBorder p='sm' radius='md' style={{ background: 'var(--mantine-color-dark-7)' }}>
          <Stack gap='sm'>
            <Text size='sm' fw={600}>
              Cake Topper Stake
            </Text>

            <Box>
              <Group justify='space-between' mb={2}>
                <Text size='xs' c='dimmed'>
                  Prong Length
                </Text>
                <Text size='xs' fw={600}>
                  {backerConfig.cakeTopperProngLengthMm} mm
                </Text>
              </Group>
              <Slider
                size='xs'
                min={30}
                max={120}
                step={5}
                value={backerConfig.cakeTopperProngLengthMm}
                onChange={(val) => updateBackerConfig({ cakeTopperProngLengthMm: val })}
              />
            </Box>

            <Box>
              <Group justify='space-between' mb={2}>
                <Text size='xs' c='dimmed'>
                  Prong Width
                </Text>
                <Text size='xs' fw={600}>
                  {backerConfig.cakeTopperProngWidthMm} mm
                </Text>
              </Group>
              <Slider
                size='xs'
                min={4}
                max={12}
                step={0.5}
                value={backerConfig.cakeTopperProngWidthMm}
                onChange={(val) => updateBackerConfig({ cakeTopperProngWidthMm: val })}
              />
            </Box>
          </Stack>
        </Paper>
      )}
    </Stack>
  );
}
